import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Card, Input, message, Modal, Popconfirm, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { deletePosHubApp, getPosHubApps, posHubConnectionPull } from 'api/poshub';
import { captureException } from 'utils/errors';
import nibbleLogoSmallUrl from 'assets/icons/nibble-logo-small.png';
import { StyledAppsTab } from './AppsTab.styles';

function isAppConnected(app) {
  return app.status === 'COMPLETED' || app.status === 'ACTIVE';
}

function isSyncCatalogRelevant(app) {
  return app.isPosApp && !app.isNibble;
}

function syncCatalogButtonTooltip(app) {
  if (!isAppConnected(app)) return 'Connect to enable catalog sync';
  if (!app.connectionId) {
    return 'POS connection id missing — refresh the list or finish connecting your POS app (same as Sync catalog in the integration UI).';
  }
  return undefined;
}

function AppCardLogo({ name, imageUrl, fallbackSrc }) {
  /** If integration remote logo URL fails (CSP, 404), try bundled fallback (e.g. Nibble marketplace app). */
  const [remoteFailed, setRemoteFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const src =
    imageUrl && !remoteFailed ? imageUrl : fallbackSrc && !fallbackFailed ? fallbackSrc : null;
  const showImg = Boolean(src);
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';

  const onImgError = () => {
    if (imageUrl && !remoteFailed) {
      setRemoteFailed(true);
    } else {
      setFallbackFailed(true);
    }
  };

  return (
    <div className="app-logo-wrap" aria-hidden>
      {showImg ? (
        <img src={src} alt="" className="app-logo" onError={onImgError} />
      ) : (
        <span className="app-logo-placeholder">{initial}</span>
      )}
    </div>
  );
}

function AppsTab({ restaurantId }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  /** Only the app whose Sync was clicked shows loading (matches applicationId from API). */
  const [syncingApplicationId, setSyncingApplicationId] = useState(null);

  const loadApps = useCallback(() => {
    if (!restaurantId) return Promise.resolve();
    setLoading(true);
    setError(null);
    return getPosHubApps(restaurantId)
      .then((res) => setApps(res.data || []))
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.detail || 'Could not load apps. Make sure this restaurant is connected to the POS integration.');
        setApps([]);
      })
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  const filteredApps = search
    ? apps.filter((a) => (a.name || '').toLowerCase().includes(search.toLowerCase()))
    : apps;

  const pointOfSaleApps = filteredApps.filter((a) => !a.isNibble);
  const marketplaceApps = filteredApps.filter((a) => a.isNibble);

  /** True if any POS app (not marketplace) is already connected */
  const connectedPosApp = apps.find((a) => a.isPosApp && !a.isNibble && isAppConnected(a));

  const handleConnect = (app) => {
    if (app.onboardingUrl) {
      window.open(app.onboardingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const copyOnboardingLink = async (app) => {
    const url = app.onboardingUrl;
    if (!url) {
      message.warning('No onboarding link available for this app.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      message.success('Onboarding link copied');
    } catch (e) {
      captureException(e);
      message.error('Could not copy link');
    }
  };

  const handleDelete = (app) => {
    if (app.isNibble) return;
    if (!app.applicationId) return;
    setDeletingId(app.applicationId);
    deletePosHubApp(restaurantId, app.applicationId)
      .then(() => {
        message.success(`${app.name} removed`);
        return loadApps();
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || 'Could not remove application');
      })
      .finally(() => setDeletingId(null));
  };

  /** POST …/connections/{connectionId}/pull (POS catalog → integration). Not Nibble sync-menus. */
  const runCatalogSync = (app) => {
    const appKey = app.applicationId;
    if (!appKey) {
      message.error('Missing application id; refresh the list and try again.');
      return Promise.reject();
    }
    if (!app.connectionId) {
      message.error(
        'No POS connection id for this app. Refresh the list or finish connecting your POS app, then try again.',
      );
      return Promise.reject();
    }
    setSyncingApplicationId(appKey);
    const done = () => setSyncingApplicationId(null);

    return posHubConnectionPull(restaurantId, app.connectionId)
      .then((data) => {
        message.success(data?.message || 'Catalog sync started');
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || 'Could not sync catalog from POS');
        throw e;
      })
      .finally(done);
  };

  const openSyncCatalogConfirm = (app) => {
    Modal.confirm({
      title: 'Sync catalog',
      content:
        'This will sync the catalog from the POS system into the integration. Are you sure you want to sync?',
      okText: 'Sync',
      cancelText: 'Cancel',
      onOk: () => runCatalogSync(app),
    });
  };

  const renderStatusFooter = (app) => {
    if (!isAppConnected(app)) return null;
    return (
      <span className="app-footer-status connected">
        <CheckCircleOutlined />
        Connected
      </span>
    );
  };

  const renderHeaderStatusIcon = (app) => {
    if (!isAppConnected(app)) return null;
    return (
      <Tooltip title="Connected">
        <span className="app-header-status-icon connected" aria-hidden>
          <CheckCircleOutlined />
        </span>
      </Tooltip>
    );
  };

  const renderAppCard = (app, idx) => {
    const posBlocked = app.isPosApp && !app.isNibble && !isAppConnected(app) && !!connectedPosApp;
    const deleteBusy = !app.applicationId || deletingId === app.applicationId;
    return (
      <Card
        key={app.applicationId || app.onboardingUrl || `${app.name}-${idx}`}
        className={`app-card${posBlocked ? ' app-card-blocked' : ''}`}
        size="small"
      >
        <div className="app-card-top">
          <div className="app-card-title-row">
            <div className="app-card-heading">
              <AppCardLogo
                name={app.name}
                imageUrl={app.imageUrl}
                fallbackSrc={app.isNibble ? nibbleLogoSmallUrl : undefined}
              />
              <span className="app-name">{app.name}</span>
            </div>
            <div className="app-card-top-actions">
              {renderHeaderStatusIcon(app)}
              {(app.dashboardUrl || app.viewUrl) && (
                <Tooltip title="View in app dashboard">
                  <Button
                    type="text"
                    size="small"
                    className="app-icon-btn"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const url = app.dashboardUrl || app.viewUrl;
                      if (url) window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title={app.onboardingUrl ? 'Copy onboarding link' : 'No onboarding link'}>
                <Button
                  type="text"
                  size="small"
                  className="app-icon-btn"
                  icon={<CopyOutlined />}
                  disabled={!app.onboardingUrl}
                  onClick={() => copyOnboardingLink(app)}
                />
              </Tooltip>
              {app.isNibble ? (
                <Tooltip title="The Nibble marketplace app cannot be removed from this location.">
                  <span>
                    <Button
                      type="text"
                      size="small"
                      danger
                      className="app-icon-btn app-delete-btn"
                      icon={<DeleteOutlined />}
                      disabled
                    />
                  </span>
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Remove this application?"
                  description="This disconnects the app from this location in the integration."
                  okText="Remove"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  disabled={deleteBusy}
                  onConfirm={() => handleDelete(app)}
                >
                  <Tooltip title={deletingId === app.applicationId ? 'Removing…' : 'Remove application'}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      className="app-icon-btn app-delete-btn"
                      icon={<DeleteOutlined />}
                      disabled={deleteBusy}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
            </div>
          </div>
          <p className="app-description">
            {app.description || `Connect ${app.name} to sync orders and catalog.`}
          </p>
        </div>
        <div className="app-card-footer">
          <div className="app-footer-left">
            {isSyncCatalogRelevant(app) && (
              <Tooltip title={syncCatalogButtonTooltip(app)}>
                <span>
                  <Button
                    type="default"
                    size="small"
                    className="app-sync-btn"
                    loading={syncingApplicationId === app.applicationId}
                    disabled={
                      !app.applicationId || !isAppConnected(app) || !app.connectionId
                    }
                    onClick={() => openSyncCatalogConfirm(app)}
                  >
                    Sync catalog
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
          <div className="app-footer-right">
            {!isAppConnected(app) ? (
              <Tooltip title={posBlocked ? 'Only 1 POS app can be connected at a time' : undefined}>
                <span>
                  <Button
                    type="primary"
                    size="small"
                    className="app-connect-btn"
                    onClick={() => handleConnect(app)}
                    disabled={!app.onboardingUrl || posBlocked}
                  >
                    Connect
                  </Button>
                </span>
              </Tooltip>
            ) : (
              renderStatusFooter(app)
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <StyledAppsTab>
      <div className="apps-toolbar">
        <Input
          placeholder="Search"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="apps-search"
          style={{ width: 200 }}
        />
      </div>
      {error && (
        <Alert
          type="warning"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {loading ? (
        <div className="apps-loading">Loading applications...</div>
      ) : (
        <>
          <div className="apps-section">
            <h2 className="apps-section-title">Point of sale</h2>
            <div className="apps-grid">
              {pointOfSaleApps.map((app, idx) => renderAppCard(app, idx))}
            </div>
          </div>
          {marketplaceApps.length > 0 && (
            <div className="apps-section">
              <h2 className="apps-section-title">Marketplace</h2>
              <div className="apps-grid">
                {marketplaceApps.map((app, idx) => renderAppCard(app, idx))}
              </div>
            </div>
          )}
        </>
      )}
    </StyledAppsTab>
  );
}

export default AppsTab;
